import { ChoiceCsvExport, ChoiceDashboardFilters, ChoiceRowsPage, ChoiceRowsRequest } from "../domain/entities/ChoiceDashboard";
import PostgisRepository from "../domain/data/PostgisRepository";
import { PostgisAssignment, PostgisProgram, PostgisSchool, SeatingStudentDetail, StudentsByDate, StudentsSpatialResult } from "../domain/entities/StudentPostgis";
import { ShapefileTag, ZoneFilter, ZoneShape } from "../presentation/components/pages/ui-utils/zoneFilterTypes";
import AwsApiClient from "../presentation/hooks/AwsApiClient";


export default class APIPostgisRepository implements PostgisRepository{
    private client = new AwsApiClient();

    async getSchoolsZone(organizationId: string): Promise<PostgisSchool[]> {
        try {
            const response = await this.client.get(
                `/api/postgis/schools/${organizationId}`,
            );
            return response;
        } catch (error) {
            const message = `An error has occured: ${error}`;
            throw new Error(message);
        }
    }

    async getGradesZone(organizationId: string): Promise<string[]> {
        try {
            const response = await this.client.get(
                `/api/postgis/grades/${organizationId}`
            );
            return response;
        } catch (error) {
            const message = `An error has occured: ${error}`;
            throw new Error(message);
        }
    }

    async getGradesZoneFiltered(organizationId: string, zoneFilter: ZoneFilter, schoolIds: string[]): Promise<string[]> {
        try {
            const response = await this.client.post(
                `/api/postgis/grades/shapefile/${organizationId}`,
                { zoneFilter, schoolIds }
            );
            return response;
        } catch (error) {
            const message = `An error has occured: ${error}`;
            throw new Error(message);
        }
    }

    async getGradesBySchools(organizationId: string, schoolIds: string[]): Promise<string[]> {
        try {
            const response = await this.client.post(
                `/api/postgis/schools/grades/${organizationId}`,
                { schoolIds }
            );
            return response;
        } catch (error) {
            const message = `An error has occured: ${error}`;
            throw new Error(message);
        }
    }

    async getProgramsZone(organizationId: string): Promise<PostgisProgram[]> {
        try {
            const response = await this.client.get(
                `/api/postgis/programs/${organizationId}`,
            );
            return response;
        } catch (error) {
            const message = `An error has occured: ${error}`;
            throw new Error(message);
        }
    }

    async getProgramsZoneFiltered(organizationId: string, zoneFilter: ZoneFilter, schoolIds: string[], grades: string[]): Promise<PostgisProgram[]> {
        try {
            const response = await this.client.post(
                `/api/postgis/programs/shapefile/${organizationId}`,
                { zoneFilter, schoolIds, grades }
            );
            return response;
        } catch (error) {
            const message = `An error has occured: ${error}`;
            throw new Error(message);
        }
    }

    async getProgramsBySchools(organizationId: string, schoolIds: string[], grades: string[]): Promise<PostgisProgram[]> {
        try {
            const response = await this.client.post(
                `/api/postgis/schools/programs/${organizationId}`,
                { schoolIds, grades }
            );
            return response;
        } catch (error) {
            const message = `An error has occured: ${error}`;
            throw new Error(message);
        }
    }

    async getAssignmentsZone(organizationId: string): Promise<PostgisAssignment[]> {
        try {
            const response = await this.client.get(
                `/api/postgis/assignments/${organizationId}`,
            );
            return response;
        } catch (error) {
            const message = `An error has occured: ${error}`;
            throw new Error(message);
        }
    }

    async getAssignmentsZoneFiltered(organizationId: string, zoneFilter: ZoneFilter, schoolIds: string[], grades: string[]): Promise<PostgisAssignment[]> {
        try {
            const response = await this.client.post(
                `/api/postgis/assignments/shapefile/${organizationId}`,
                { zoneFilter, schoolIds, grades }
            );
            return response;
        } catch (error) {
            const message = `An error has occured: ${error}`;
            throw new Error(message);
        }
    }

    async getAssignmentsBySchools(organizationId: string, schoolIds: string[], grades: string[]): Promise<PostgisAssignment[]> {
        try {
            const response = await this.client.post(
                `/api/postgis/schools/assignments/${organizationId}`,
                { schoolIds, grades }
            );
            return response;
        } catch (error) {
            const message = `An error has occured: ${error}`;
            throw new Error(message);
        }
    }

    async getPostgisStudentsZone(
        organizationId: string,
        zoneFilter: ZoneFilter,
        schoolIds: string[],
        grades: string[],
        programs: string[],
        assignments: string[]
    ): Promise<{ students: StudentsSpatialResult[], excludedCount: number }> {
        try {
            const response = await this.client.post(
                `/api/postgis/students/shapefile/${organizationId}`,
                { zoneFilter, schoolIds, grades, programs, assignments }
            );
            return response;
        } catch (error) {
            const message = `An error has occured: ${error}`;
            throw new Error(message);
        }
    }

    async getPostgisStudentsByFilters(
        organizationId: string,
        schoolIds: string[],
        grades: string[],
        programs: string[]
    ): Promise<{ students: StudentsSpatialResult[], excludedCount: number }> {
        try {
            const response = await this.client.post(
                `/api/postgis/students/filtered/${organizationId}`,
                { schoolIds, grades, programs }
            );
            return response;
        } catch (error) {
            const message = `An error has occured: ${error}`;
            throw new Error(message);
        }
    }

    async saveShapefiles(organizationId: string, shapes: ZoneShape[]): Promise<ZoneShape[]> {
        try {
            const response = await this.client.post(
                `/api/postgis/shapefiles/${organizationId}`,
                shapes
            );

            return response;
        } catch (error) {
            const message = `An error has occured: ${error}`;
            throw new Error(message);
        }
    }

    async updateShapefileName(organizationId: string, shapefileId: string, name: string): Promise<any> {
        try {
            const response = await this.client.post(
                `/api/postgis/shapefiles/${organizationId}/${shapefileId}/name`,
                { name }
            );
            return response;
        } catch (error) {
            const message = `An error has occured: ${error}`;
            throw new Error(message);
        }
    }

    async updateShapefileTags(organizationId: string, shapefileId: string, tags: ShapefileTag[]): Promise<any> {
        try {
            const response = await this.client.post(
                `/api/postgis/shapefiles/${organizationId}/${shapefileId}/tags`,
                { tags }
            );
            return response;
        } catch (error) {
            const message = `An error has occured: ${error}`;
            throw new Error(message);
        }
    }

    async updateShapefileDescription(organizationId: string, shapefileId: string, description: string): Promise<any> {
        try {
            const response = await this.client.post(
                `/api/postgis/shapefiles/${organizationId}/${shapefileId}/description`,
                { description }
            );
            return response;
        } catch (error) {
            const message = `An error has occured: ${error}`;
            throw new Error(message);
        }
    }

  async loadShapefiles(organizationId: string): Promise<ZoneShape[]> {
    try {
        const response = await this.client.get(
        `/api/postgis/shapefile/${organizationId}`
        );

        return response;
    } catch (error) {
        const message = `An error has occured: ${error}`;
        throw new Error(message);
    }
  }

  async loadShapefilesList(organizationId: string): Promise<ZoneShape[]> {
    try {
        const response = await this.client.get(
            `/api/postgis/shapefiles/list/${organizationId}`
        );
        return response;
    } catch (error) {
        const message = `An error has occured: ${error}`;
        throw new Error(message);
    }
  }

  async loadShapefileGeometry(organizationId: string, shapefileId: string): Promise<ZoneShape> {
    try {
        const response = await this.client.get(
            `/api/postgis/shapefiles/geometry/${organizationId}/${shapefileId}`
        );
        return response;
    } catch (error) {
        const message = `An error has occured: ${error}`;
        throw new Error(message);
    }
  }

  async deleteShapefile(organizationId: string, shapefileId: string): Promise<boolean> {
    try {
        const response = await this.client.delete(
            `/api/postgis/shapefile/${organizationId}/${shapefileId}`,
            "none"
        );

        return true;
    } catch (error) {
        const message = `An error has occured: ${error}`;
        throw new Error(message);
    }
  }

  async getArchiveDates(organizationId: string): Promise<string[]> {
    try {
        const response = await this.client.get(
            `/api/postgis/archives/dates/${organizationId}`
        );
        return response;
    } catch (error) {
        const message = `An error has occured: ${error}`;
        throw new Error(message);
    }
  }

  async getArchivedStudentNumberForGradesAndPrograms(organizationId: string, archiveDate: string, schoolId: string): Promise<StudentsByDate> {
    try {
        const response = await this.client.get(
            `/api/postgis/archives/students/grades/programs/number/${organizationId}/${archiveDate}/${schoolId}`
        );
        return response;
    } catch (error) {
        const message = `An error has occured: ${error}`;
        throw new Error(message);
    }
  }

  async getCompareStudentsCountByGradeAndProgram(organizationId: string, archiveDate1: string, archiveDate2: string, schoolId: string): Promise<StudentsByDate> {
    try {
        const response = await this.client.get(
            `/api/postgis/archives/students/number/compare/${organizationId}/${archiveDate1}/${archiveDate2}/${schoolId}`
        );
        return response;
    } catch (error) {
        const message = `An error has occured: ${error}`;
        throw new Error(message);
    }
  }

  async getArchivedStudentNumberForGradesAndProgramsAllSchools(organizationId: string, archiveDate: string): Promise<StudentsByDate> {
    try {
        const response = await this.client.get(
            `/api/postgis/archives/students/grades/programs/number/${organizationId}/${archiveDate}`
        );
        return response;
    } catch (error) {
        const message = `An error has occured: ${error}`;
        throw new Error(message);
    }
  }

  async getCompareStudentsCountByGradeAndProgramAllSchools(organizationId: string, archiveDate1: string, archiveDate2: string): Promise<StudentsByDate> {
    try {
        const response = await this.client.get(
            `/api/postgis/archives/students/number/compare/${organizationId}/${archiveDate1}/${archiveDate2}`
        );
        return response;
    } catch (error) {
        const message = `An error has occured: ${error}`;
        throw new Error(message);
    }
  }

  async getStudentsCountBySchoolGradeAndProgram(organizationId: string, archiveDate: string): Promise<any> {
    try {
        const response = await this.client.get(
            `/api/postgis/archives/students/by-school/${organizationId}/${archiveDate}`
        );
        return response;
    } catch (error) {
        const message = `An error has occured: ${error}`;
        throw new Error(message);
    }
  }

  async getProgramDemand(organizationId: string): Promise<any> {
    try {
        const response = await this.client.get(
            `/api/postgis/program-demand/${organizationId}`
        );
        return response;
    } catch (error) {
        const message = `An error has occured: ${error}`;
        throw new Error(message);
    }
  }

  async getProgramApplicants(organizationId: string, program: string): Promise<any[]> {
    try {
        const response = await this.client.post(
            `/api/postgis/program-demand/applicants/${organizationId}`,
            { program }
        );
        return response;
    } catch (error) {
        const message = `An error has occured: ${error}`;
        throw new Error(message);
    }
  }

  async getChoiceDashboard(organizationId: string, filters: ChoiceDashboardFilters): Promise<any> {
    // Not wrapped: the backend's {error} text (or the client's) is what the page shows.
    const response = await this.client.post(
        `/api/choice/dashboard/${organizationId}`,
        filters || {}
    );
    return response;
  }

  async getChoiceRows(organizationId: string, request: ChoiceRowsRequest): Promise<ChoiceRowsPage> {
    // Not wrapped: the backend's {error} text (or the client's) is what the page shows.
    const response = await this.client.post(
        `/api/choice/rows/${organizationId}`,
        request || {}
    );
    return response;
  }

  async exportChoiceRows(organizationId: string, filters: ChoiceDashboardFilters): Promise<ChoiceCsvExport> {
    // Not wrapped: the backend's {error} text (or the client's) is what the page shows.
    // JSON envelope {csv, rows, truncated}: the gateway caps a response at 10 MB and the
    // page must be able to tell the user when the file was cut there.
    const response = await this.client.post(
        `/api/choice/export/${organizationId}`,
        filters || {}
    );
    return response;
  }

  async startChoiceImport(organizationId: string): Promise<any> {
    // No wrapping here: the backend's 409 explains the configuration problem
    // and the page shows that text as-is.
    return this.client.post(`/api/choice/import/${organizationId}`, {});
  }

  async getChoiceYears(organizationId: string): Promise<{ schoolYears: string[] }> {
    return this.client.get(`/api/choice/years/${organizationId}`);
  }

  async getChoiceImportStatus(organizationId: string): Promise<any> {
    // Not wrapped: the backend's {error} text (or the client's) is what the page shows.
    const response = await this.client.get(
        `/api/choice/import-status/${organizationId}`
    );
    return response;
  }

  async getExitingStudents(organizationId: string, school: string): Promise<any[]> {
    try {
        const response = await this.client.post(
            `/api/postgis/program-demand/exiting/${organizationId}`,
            { school }
        );
        return response;
    } catch (error) {
        const message = `An error has occured: ${error}`;
        throw new Error(message);
    }
  }

  async getProjectedEnrollment(organizationId: string, archiveDate: string, years: number, growthRate?: number): Promise<any> {
    try {
        let url = `/api/postgis/archives/students/projected/${organizationId}/${archiveDate}/${years}`;
        if (growthRate !== undefined) {
            url += `?growthRate=${growthRate}`;
        }
        const response = await this.client.get(url);
        return response;
    } catch (error) {
        const message = `An error has occured: ${error}`;
        throw new Error(message);
    }
  }

  async getSESBreakdownBySchool(organizationId: string, archiveDate: string): Promise<any[]> {
    try {
        const response = await this.client.get(
            `/api/postgis/archives/students/ses-breakdown/${organizationId}/${archiveDate}`
        );
        return response;
    } catch (error) {
        const message = `An error has occured: ${error}`;
        throw new Error(message);
    }
  }

  async getSESBreakdownByZone(organizationId: string, zoneFilter: ZoneFilter): Promise<any[]> {
    try {
        const response = await this.client.post(
            `/api/postgis/students/ses-breakdown/zone/${organizationId}`,
            { zoneFilter }
        );
        return response;
    } catch (error) {
        const message = `An error has occured: ${error}`;
        throw new Error(message);
    }
  }

  async getCompareStudentsCountBySchoolGradeAndProgram(organizationId: string, archiveDate1: string, archiveDate2: string): Promise<any> {
    try {
        const response = await this.client.get(
            `/api/postgis/archives/students/by-school/compare/${organizationId}/${archiveDate1}/${archiveDate2}`
        );
        return response;
    } catch (error) {
        const message = `An error has occured: ${error}`;
        throw new Error(message);
    }
  }

  async getStudentsCountByZone(organizationId: string, zoneFilter: ZoneFilter): Promise<{ counts: any[]; totalInZone: number; excludedCount: number; averageSes: number | null; sesDistribution: Record<string, number>; inTransportationZone: number; outsideTransportationZone: number; transportationZoneBreakdown: Record<string, number> }> {
    try {
        const response = await this.client.post(
            `/api/postgis/students/counts/zone/${organizationId}`,
            { zoneFilter }
        );
        return response;
    } catch (error) {
        const message = `An error has occured: ${error}`;
        throw new Error(message);
    }
  }

    async getSeatingStudentDetails(
        organizationId: string,
        payload: { archiveDate?: string; schoolIds?: string[]; grades?: string[]; programs?: string[]; zoneFilter?: ZoneFilter | null; offset?: number; limit?: number }
    ): Promise<SeatingStudentDetail[]> {
        try {
            const response = await this.client.post(
                `/api/postgis/students/details/${organizationId}`,
                payload
            );
            return response;
        } catch (error) {
            const message = `An error has occured: ${error}`;
            throw new Error(message);
        }
    }

  async askZoneAI(organizationId: string, payload: any): Promise<any> {
    const startResult = await this.client.post(
        `/api/postgis/zone-ai/start/${organizationId}`, payload);

    if (startResult.error) {
        throw new Error(startResult.error);
    }

    const jobId = startResult.jobId;
    if (!jobId) {
        throw new Error("Failed to start AI processing.");
    }

    const POLL_INTERVAL = 2000;
    const MAX_POLL_TIME = 300000;
    const startTime = Date.now();

    while (Date.now() - startTime < MAX_POLL_TIME) {
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL));

        const status = await this.client.get(`/api/postgis/zone-ai/status/${jobId}`);

        if (status.status === "complete" || status.status === "error") {
            return status.result;
        }

        if (status.status === "not_found") {
            throw new Error("Job not found. Please try again.");
        }
    }

    throw new Error("Request timed out after 5 minutes. Please try again.");
  }

  async summarizeZoneAI(organizationId: string, payload: any): Promise<any> {
    try {
        return await this.client.post(`/api/postgis/zone-ai/summarize/${organizationId}`, payload);
    } catch (error) {
        throw new Error(`An error has occured: ${error}`);
    }
  }

  async geocodeAddress(organizationId: string, payload: { address: string }): Promise<any> {
    try {
        return await this.client.post(`/api/postgis/zone-ai/geocode/${organizationId}`, payload);
    } catch (error) {
        throw new Error(`An error has occured: ${error}`);
    }
  }

  async combineZones(organizationId: string, payload: { zoneNames: string[]; operation: string }): Promise<any> {
    try {
        return await this.client.post(`/api/postgis/zone-ai/combine/${organizationId}`, payload);
    } catch (error) {
        throw new Error(`An error has occured: ${error}`);
    }
  }

  async bufferZone(organizationId: string, payload: { zoneName: string; distance: number; unit: string }): Promise<any> {
    try {
        return await this.client.post(`/api/postgis/zone-ai/buffer/${organizationId}`, payload);
    } catch (error) {
        throw new Error(`An error has occured: ${error}`);
    }
  }

  async scaleZone(organizationId: string, payload: { zoneName: string; percentage: number }): Promise<any> {
    try {
        return await this.client.post(`/api/postgis/zone-ai/scale/${organizationId}`, payload);
    } catch (error) {
        throw new Error(`An error has occured: ${error}`);
    }
  }
}